import ArrayUtils from '../common/utils/ArrayUtils';
import CoreUtils from '../common/utils/CoreUtils';

import DataModel from '../common/model/DataModel';

import MemoApi from '../api-services/MemoApi';

import MemoEditorDataModel from './MemoEditorDataModel';

const refreshTimeout = 3000;

export default class MemoPaneDataModel extends DataModel {
  constructor(locale, counterparty) {
    super();

    this.locale = locale;
    this.counterparty = counterparty;
    this.memoDataModelIndex = {};

    this.refreshCounterpartyMemosHandler = this.refreshCounterpartyMemos.bind(this);
    this.refreshTimeoutId = null;

    this.editorRights = {
      canEditPowerexMemos: this.counterparty.canEditPowerexMemos,
      canEditExternalMemos: this.counterparty.canEditExternalMemos
    };

    this.allowEditMode = this.editorRights.canEditExternalMemos
      || this.editorRights.canEditPowerexMemos;

    this.editModeActive = false;
    this.isModified = false;
    this.memoDataModels = [];

    this.bufferedMemoDtos = [];
    this.softDeletedMemoGuids = [];

    this.refreshCounterpartyMemos();
  }

  dispose() {
    this.cancelRefreshTimeout();

    CoreUtils.iterateProperties(this.memoDataModelIndex, (guid, dataModel) => {
      dataModel.dispose();
    });

    super.dispose();
  }

  refreshCounterpartyMemos() {
    MemoApi.getCounterpartyMemos(this.counterparty.code)
      .then(
        this.onGetCounterpartyMemosSucceeded.bind(this),
        this.onGetCounterpartyMemosFailed.bind(this)
      );
  }

  triggerRefreshTimeout() {
    this.refreshTimeoutId = setTimeout(this.refreshCounterpartyMemosHandler, refreshTimeout);
  }

  cancelRefreshTimeout() {
    if (this.refreshTimeoutId === null)
      return;

    clearTimeout(this.refreshTimeoutId);
    this.refreshTimeoutId = null;
  }

  onGetCounterpartyMemosSucceeded(result) {
    this.refreshTimeoutId = null;
    this.bufferedMemoDtos = result;

    if (!this.isModified)
      this.processMemoUpdate(result);

    this.triggerRefreshTimeout();
  }

  onGetCounterpartyMemosFailed(result) {
    this.refreshTimeoutId = null;
    this.triggerRefreshTimeout();
  }

  processMemoUpdate(memoDtos) {
    var deleteGuids = CoreUtils.getObjectPropertyNames(this.memoDataModelIndex);

    memoDtos.forEach(memoDto => {
      var dataModel = null;

      if (this.memoDataModelIndex[memoDto.guid] === undefined) {
        dataModel = new MemoEditorDataModel(
          this.locale,
          this.counterparty,
          this,
          this.editorRights,
          memoDto,
          false
        );
        this.memoDataModelIndex[memoDto.guid] = dataModel;
      } else {
        dataModel = this.memoDataModelIndex[memoDto.guid];
        dataModel.updateMemoDto(memoDto, false);
      }

      var index = deleteGuids.indexOf(memoDto.guid);
      if (index !== -1)
        deleteGuids.splice(index, 1);
    });

    deleteGuids.forEach(deleteGuid => {
      var dataModel = this.memoDataModelIndex[deleteGuid];
      if (CoreUtils.isDefinedAndNotNull(dataModel)) {
        dataModel.dispose();
        delete this.memoDataModelIndex[deleteGuid];
      }
    });

    this.softDeletedMemoGuids = [];
    this.updateMemoView(false);
  }

  updateMemoView(makeEditModeActive) {
    var memoDataModels = [];

    CoreUtils.iterateProperties(
      this.memoDataModelIndex,
      (guid, dataModel) => memoDataModels.push(dataModel)
    );

    memoDataModels.sort(this.orderCompareDataModel);

    this.updateProperties({
      memoDataModels: memoDataModels
    });

    this.updateMemoOrderOperations(memoDataModels);

    this.updateProperties({
      editModeActive: (this.editModeActive || makeEditModeActive)
        && this.allowEditMode
        && memoDataModels.length > 0,
      isModified: this.resolveIsModified()
    });
  }

  orderCompareDataModel(dataModel1, dataModel2) {
    if (dataModel1.currentDisplayOrderIndex > dataModel2.currentDisplayOrderIndex)
      return 1;
    if (dataModel1.currentDisplayOrderIndex < dataModel2.currentDisplayOrderIndex)
      return -1;

    return 0;
  }

  updateMemoOrderOperations(memoDataModels) {
    for (var index = 0; index < memoDataModels.length; index++) {
      var dataModel = memoDataModels[index];
      dataModel.setDisplayOrderIndex(
        dataModel.currentDisplayOrderIndex,
        index > 0,
        index < memoDataModels.length - 1
      );
    }
  }

  onMemoIsModifiedChanged() {
    this.updateProperties({
      isModified: this.resolveIsModified()
    });
  }

  onMemoMoveUp(memoDataModel) {
    var pivotIndex = this.memoDataModels.indexOf(memoDataModel);
    if (pivotIndex === -1)
      return;

    var swapIndex = pivotIndex - 1;
    if (swapIndex < 0)
      return;

    this.swapMemoEditors(
      memoDataModel,
      pivotIndex,
      this.memoDataModels[swapIndex],
      swapIndex
    );
  }

  onMemoMoveDown(memoDataModel) {
    var pivotIndex = this.memoDataModels.indexOf(memoDataModel);
    if (pivotIndex === -1)
      return;

    var swapIndex = pivotIndex + 1;
    if (swapIndex >= this.memoDataModels.length)
      return;

    this.swapMemoEditors(
      memoDataModel,
      pivotIndex,
      this.memoDataModels[swapIndex],
      swapIndex
    );
  }

  swapMemoEditors(pivotDataModel, pivotIndex, swapDataModel, swapIndex) {
    var swapDisplayOrderIndex = swapDataModel.currentDisplayOrderIndex;
    var swapEnableMoveUp = swapDataModel.enableMoveUp;
    var swapEnableMoveDown = swapDataModel.enableMoveDown;

    swapDataModel.setDisplayOrderIndex(
      pivotDataModel.currentDisplayOrderIndex,
      pivotDataModel.enableMoveUp,
      pivotDataModel.enableMoveDown
    );

    pivotDataModel.setDisplayOrderIndex(
      swapDisplayOrderIndex,
      swapEnableMoveUp,
      swapEnableMoveDown
    );

    var memoDataModels = Array.from(this.memoDataModels);

    memoDataModels[pivotIndex] = swapDataModel;
    memoDataModels[swapIndex] = pivotDataModel;

    this.updateProperties({
      memoDataModels: memoDataModels
    });
  }

  onMemoDelete(memoGuid) {
    var dataModel = this.memoDataModelIndex[memoGuid];
    if (CoreUtils.isUndefinedOrNull(dataModel))
      return;

    if (!dataModel.isNew)
      this.softDeletedMemoGuids.push(memoGuid);

    dataModel.dispose();
    delete this.memoDataModelIndex[memoGuid];

    var memoDataModels = Array.from(this.memoDataModels);
    var dataModelIndex = memoDataModels.indexOf(dataModel);
    if (dataModelIndex !== -1)
      memoDataModels.splice(dataModelIndex, 1);

    this.updateProperties({
      memoDataModels: memoDataModels,
    });

    this.updateMemoOrderOperations(memoDataModels);

    this.updateProperties({
      isModified: this.resolveIsModified()
    });
  }

  onOpenEditor() {
    if (!this.allowEditMode)
      return;

    this.updateProperties({
      editModeActive: true
    });
  }

  onCloseEditor() {
    this.onRevertChanges();

    this.updateProperties({
      editModeActive: false
    });
  }

  onCreateMemo() {
    if (!this.allowEditMode)
      return;

    var displayOrderIndex = ArrayUtils.maximumValue(
      this.memoDataModels.map(dataModel => dataModel.currentDisplayOrderIndex),
      -1
    ) + 1;

    var memoDto = {
      counterpartyCode: this.counterparty.Code,
      guid: crypto.randomUUID(),
      onlyVisibleToPowerex: !this.editorRights.canEditExternalMemos,
      displayOrderIndex: displayOrderIndex,
      htmlContent: '<p>This is a new memo.</p>'
    };

    var newDataModel = new MemoEditorDataModel(
      this.locale,
      this.counterparty,
      this,
      this.editorRights,
      memoDto,
      true
    );
    this.memoDataModelIndex[memoDto.guid] = newDataModel;

    this.updateMemoView(true);
  }

  async onSaveChanges() {
    this.cancelRefreshTimeout();

    var postMemoDtos = this.memoDataModels
      .filter(dataModel => dataModel.isNew)
      .map(dataModel => dataModel.getMemoDto());

    if (postMemoDtos.length > 0) {
      var postResult = MemoApi.postCounterpartyMemos(postMemoDtos);
      this.updateMemoDataModels(await postResult);
    }

    var putMemoDtos = this.memoDataModels
      .filter(dataModel => dataModel.isModified && !dataModel.isNew)
      .map(dataModel => dataModel.getMemoDto());

    if (putMemoDtos.length > 0) {
      var putResult = MemoApi.putCounterpartyMemos(putMemoDtos);
      this.updateMemoDataModels(await putResult);
    }

    var deleteMemoGuids = Array.from(this.softDeletedMemoGuids);

    for (var index = 0; index < deleteMemoGuids.length; index++) {
      if (await MemoApi.deleteCounterpartyMemo(deleteMemoGuids[index]))
        ArrayUtils.removeElement(this.softDeletedMemoGuids, deleteMemoGuids[index]);
    }

    this.updateProperties({
      isModified: this.resolveIsModified()
    });

    this.refreshCounterpartyMemos();
  }

  onRevertChanges() {
    this.processMemoUpdate(this.bufferedMemoDtos);
  }

  resolveIsModified() {
    return this.memoDataModels.some(dataModel => dataModel.isModified)
      || this.softDeletedMemoGuids.length > 0;
  }

  updateMemoDataModels(memoDtos) {
    if (!Array.isArray(memoDtos))
      return;

    memoDtos.forEach(memoDto => {
      var dataModel = this.memoDataModelIndex[memoDto.guid];
      if (CoreUtils.isDefinedAndNotNull(dataModel))
        dataModel.updateMemoDto(memoDto);
    });
  }
}